/**
 * Insert-geometry — pure geometry helpers for overlay insertion.
 *
 * Extracted from insert-flow.js per ts-unit-testability-seams-260623 §T:
 * `InsertRect` / `SnapResult` are the typed value-objects for the geometry
 * seam. Pure module: no DOM, no side effects. Runs in node unit tests.
 *
 * Exported types:
 *   InsertRect       — { x, y, w, h } rectangle in canvas pixels.
 *   SnapResult       — InsertRect + per-edge snap flags.
 *   CardAnchorInfo   — { structId, page, bbox } for sibling-above lookup.
 *
 * Exported functions:
 *   snapEdgesToBoxes(rect, boxes, tolerance) → SnapResult
 *   defaultInsertSize(page, clickPt)         → InsertRect
 *   findClosestSiblingAbove(newBbox, cards, page) → CardAnchorInfo | null
 *   nextEditorInsertId()                     → string
 *
 * @module insert-geometry
 */
const SNAP_TOLERANCE_PX = 6;
const MIN_INSERT_PX = 10;
const DEFAULT_WIDTH_FRACTION = 1 / 3; // §10.6.2 — ~1/3 of page width
const DEFAULT_HEIGHT_FRACTION = 1 / 10; // §10.6.2 — ~1/10 of page height
// ─── Pure helpers ────────────────────────────────────────────────────────────
/**
 * Snap rubber-band rect edges to the nearest existing bbox edge within
 * `tolerance` pixels. Returns a new rect; original is untouched.
 *
 * Each edge of the rubber band (left/right/top/bottom) is independently
 * snapped to the closest matching edge of any existing bbox.
 */
export function snapEdgesToBoxes(rect, boxes, tolerance) {
    const tol = typeof tolerance === "number" ? tolerance : SNAP_TOLERANCE_PX;
    const { x, y, w, h } = rect;
    let left = x;
    let right = x + w;
    let top = y;
    let bottom = y + h;
    const snapped = { left: false, right: false, top: false, bottom: false };
    if (Array.isArray(boxes)) {
        for (const b of boxes) {
            if (!b)
                continue;
            const bL = b.x;
            const bR = b.x + b.w;
            const bT = b.y;
            const bB = b.y + b.h;
            // Snap left edge to either bL or bR
            if (!snapped.left && Math.abs(left - bL) <= tol) {
                left = bL;
                snapped.left = true;
            }
            else if (!snapped.left && Math.abs(left - bR) <= tol) {
                left = bR;
                snapped.left = true;
            }
            if (!snapped.right && Math.abs(right - bR) <= tol) {
                right = bR;
                snapped.right = true;
            }
            else if (!snapped.right && Math.abs(right - bL) <= tol) {
                right = bL;
                snapped.right = true;
            }
            if (!snapped.top && Math.abs(top - bT) <= tol) {
                top = bT;
                snapped.top = true;
            }
            else if (!snapped.top && Math.abs(top - bB) <= tol) {
                top = bB;
                snapped.top = true;
            }
            if (!snapped.bottom && Math.abs(bottom - bB) <= tol) {
                bottom = bB;
                snapped.bottom = true;
            }
            else if (!snapped.bottom && Math.abs(bottom - bT) <= tol) {
                bottom = bT;
                snapped.bottom = true;
            }
        }
    }
    // Preserve sane ordering (left ≤ right, top ≤ bottom)
    if (right < left) {
        const tmp = left;
        left = right;
        right = tmp;
    }
    if (bottom < top) {
        const tmp = top;
        top = bottom;
        bottom = tmp;
    }
    return {
        x: left,
        y: top,
        w: Math.max(MIN_INSERT_PX, right - left),
        h: Math.max(MIN_INSERT_PX, bottom - top),
        snapped,
    };
}
/**
 * Default insertion dimensions for the right-click path. Per §10.6.2:
 * width = page.width × 1/3, height = page.height × 1/10. Anchored so the
 * bbox is centered horizontally on the click x and aligned with its top
 * edge at the click y. Clamped to page bounds.
 */
export function defaultInsertSize(page, clickPt) {
    const w = Math.max(MIN_INSERT_PX, page.width * DEFAULT_WIDTH_FRACTION);
    const h = Math.max(MIN_INSERT_PX, page.height * DEFAULT_HEIGHT_FRACTION);
    let x = clickPt.x - w / 2;
    let y = clickPt.y;
    // Clamp to page bounds.
    if (x < 0)
        x = 0;
    if (y < 0)
        y = 0;
    if (x + w > page.width)
        x = page.width - w;
    if (y + h > page.height)
        y = page.height - h;
    return { x, y, w, h };
}
/**
 * Find the existing card that is the closest sibling ABOVE-by-y-coordinate
 * (smaller `y` = higher on the page in canvas-px terms) on the same page.
 * If none, returns null — caller should anchor at the page's first slot.
 */
export function findClosestSiblingAbove(newBbox, cards, page) {
    if (!newBbox || !Array.isArray(cards))
        return null;
    let best = null;
    let bestTop = -Infinity;
    for (const c of cards) {
        if (!c || c.page !== page || !c.bbox)
            continue;
        const cTop = c.bbox.y;
        // "Above" in canvas px = smaller y. We want the one whose top is the
        // greatest y that is still ≤ newBbox.y.
        if (cTop <= newBbox.y && cTop > bestTop) {
            bestTop = cTop;
            best = c;
        }
    }
    return best;
}
/**
 * Generate a fresh editor-side id for an UNDEFINED insertion. These ids
 * never reach the backend — they are local-only until type-resolved.
 */
let _editorIdSeq = 0;
export function nextEditorInsertId() {
    _editorIdSeq += 1;
    return `editor-insert-${Date.now().toString(36)}-${_editorIdSeq}`;
}
